import React from "react";
import { Trash2 } from "lucide-react";
import { useQuestionOptions } from "./questionOptions";
import { validateInputValue, GuidanceBlock } from "./displayConditionUtils";

const QuestionInput = ({
  field,
  value,
  onChange,
  onOptionsLoaded,
  disabled,
}) => {
  const { options, loading } = useQuestionOptions(field, onOptionsLoaded);
  const type = field.questionType || field.fieldType;
  const isPercentage =
    type === "percentage" ||
    (field.questionText || field.fieldLabel || "")
      .toLowerCase()
      .includes("percentage");

  if (loading)
    return <span className="text-gray-400 text-sm">Loading options...</span>;
  
  switch (type) {
    case "text":
      return (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
          placeholder={`Enter ${field.questionText || field.fieldLabel}`}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
        />
      );

    case "number":
    case "percentage":
      const rangeLimit =
        field.limit && field.limit.length > 0 ? field.limit[0] : null;

      return (
        <div className="relative">
          <input
            type="number"
            min="0"
            value={value ?? ""}
            disabled={disabled}
            onChange={(e) => {
              onChange(validateInputValue(field, e.target.value));
            }}
            className={`w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none ${
              disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
            }`}
            placeholder={
              rangeLimit
                ? `Range ${rangeLimit}`
                : isPercentage
                  ? "0-100"
                  : "Enter value"
            }
          />
        </div>
      );
    case "select":
      return (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white outline-none"
        >
          <option value="">-- Select --</option>
          {options.map((opt) => (
            <option key={opt._id || opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "multiselect": {
      const current = Array.isArray(value) ? value : [];
      const hasHeaders = options.some((opt) => !opt.value || opt.value === "");

      if (!hasHeaders) {
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {options.map((opt) => (
              <label
                key={opt._id || opt.value}
                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
              >
                <input
                  type="checkbox"
                  checked={current.includes(opt.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...current, opt.value]);
                    } else {
                      onChange(current.filter((v) => v !== opt.value));
                    }
                  }}
                  className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
                />
                <span className="text-gray-700 text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        );
      }

      const groupedOptions = [];
      let currentGroup = { header: null, options: [] };

      options.forEach((opt) => {
        if (!opt.value || opt.value === "") {
          if (currentGroup.options.length > 0) {
            groupedOptions.push(currentGroup);
          }
          currentGroup = { header: opt.label, options: [] };
        } else {
          currentGroup.options.push(opt);
        }
      });

      if (currentGroup.options.length > 0) {
        groupedOptions.push(currentGroup);
      }

      return (
        <div className="space-y-4">
          {groupedOptions.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              {group.header && (
                <div className="font-semibold text-gray-800 text-sm uppercase tracking-wide ">
                  {group.header}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-0">
                {group.options.map((opt) => (
                  <label
                    key={opt._id || opt.value}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={current.includes(opt.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          onChange([...current, opt.value]);
                        } else {
                          onChange(current.filter((v) => v !== opt.value));
                        }
                      }}
                      className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    case "file": {
      const files = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-3">
          <input
            type="file"
            multiple
            onChange={(e) => {
              const newFiles = Array.from(e.target.files || []);
              onChange([...files, ...newFiles]);
            }}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer"
          />
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Uploaded Files:
              </p>
              <ul className="space-y-1">
                {files.map((file, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200"
                  >
                    <span className="text-sm text-gray-700 truncate">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onChange(files.filter((_, i) => i !== idx))
                      }
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

export default QuestionInput;
